import React, { useState } from "react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";

const INITIAL = {
  business_name: "",
  contact_name: "",
  phone: "",
  email: "",
  message: "",
};

const FIELDS = [
  { name: "business_name", label: "Business Name", type: "text", placeholder: "Acme Industrial Co." },
  { name: "contact_name", label: "Contact Name", type: "text", placeholder: "Full name" },
  { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567" },
  { name: "email", label: "Email Address", type: "email", placeholder: "you@company.com" },
];

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.detail?.[0]?.msg ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="relative py-28 sm:py-36 px-6 sm:px-10"
      style={{ background: "var(--kz-bg-secondary)" }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <div className="lg:col-span-5">
            <span
              className="font-display text-xs uppercase tracking-[0.4em] inline-block mb-4"
              style={{ color: "var(--kz-accent)" }}
            >
              [ 03 ] Request a Quote
            </span>
            <h2
              data-testid="contact-heading"
              className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-8"
              style={{ color: "var(--kz-text-primary)" }}
            >
              Let&apos;s discuss
              <br />
              <span style={{ color: "var(--kz-accent)" }}>your site.</span>
            </h2>
            <p
              className="text-base sm:text-lg leading-relaxed mb-12 max-w-md"
              style={{ color: "var(--kz-text-secondary)" }}
            >
              Tell us about your operation and where you need support.
              Our team will respond with a tailored proposal built around your
              fleet, sites, and crews.
            </p>

            <div className="space-y-4 pt-8 border-t" style={{ borderColor: "var(--kz-border)" }}>
              <div>
                <div
                  className="font-display text-[11px] uppercase tracking-[0.3em] mb-1"
                  style={{ color: "var(--kz-text-secondary)", opacity: 0.7 }}
                >
                  Direct Inquiries
                </div>
                <a
                  href="mailto:service@kzenvironmental.com"
                  data-testid="contact-email-link"
                  className="font-display text-lg sm:text-xl hover:underline underline-offset-4 decoration-1"
                  style={{ color: "var(--kz-accent)" }}
                >
                  service@kzenvironmental.com
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="space-y-7"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                {FIELDS.map((field) => (
                  <div key={field.name} className="flex flex-col">
                    <label
                      htmlFor={field.name}
                      className="font-display text-[11px] uppercase tracking-[0.3em] mb-3"
                      style={{ color: "var(--kz-text-secondary)" }}
                    >
                      {field.label}
                    </label>
                    <input
                      data-testid={`input-${field.name}`}
                      id={field.name}
                      name={field.name}
                      type={field.type}
                      value={form[field.name]}
                      onChange={onChange}
                      placeholder={field.placeholder}
                      required
                      className="kz-input font-body text-base px-4 py-4 rounded-none"
                    />
                  </div>
                ))}
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="message"
                  className="font-display text-[11px] uppercase tracking-[0.3em] mb-3"
                  style={{ color: "var(--kz-text-secondary)" }}
                >
                  Message
                </label>
                <textarea
                  data-testid="input-message"
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  required
                  placeholder="Tell us about your property, site requirements, and timing..."
                  className="kz-input font-body text-base px-4 py-4 rounded-none resize-none"
                />
              </div>

              <div className="pt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="cta-button group inline-flex items-center gap-4 px-10 py-5 font-display text-xs uppercase tracking-[0.3em] font-semibold rounded-none disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: "var(--kz-accent)",
                    color: "var(--kz-bg)",
                    border: "1px solid var(--kz-accent)",
                  }}
                >
                  <span className="relative z-10">
                    {submitting ? "Sending..." : "Send Inquiry"}
                  </span>
                  <span
                    className="relative z-10 inline-block w-8 h-px transition-transform duration-500 group-hover:translate-x-1"
                    style={{ background: "var(--kz-bg)" }}
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
